import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, message, Select } from 'antd';
import { ApiOutlined } from '@ant-design/icons';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

import { useActiveOrganization } from '../../hooks';
import { useNodeResources } from '@components/flows/FlowBuilder/useNodeResources.query';
import {
  useCreateConnectionMutation,
  useUpdateConnectionMutation,
} from '../../pages/ConnectionsPage/ConnectionsPage.query';

import styles from './ConnectionModal.module.scss';

const { TextArea } = Input;

type ConnectionModalMode = 'create' | 'edit';

interface ConnectionModalProps {
  mode: ConnectionModalMode;
  visible: boolean;
  connection?: Connection | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const ConnectionModal: React.FC<ConnectionModalProps> = ({
  mode,
  visible,
  connection,
  onClose,
  onSuccess,
}) => {
  const { _ } = useLingui();
  const [form] = Form.useForm();
  const createConnectionMutation = useCreateConnectionMutation();
  const updateConnectionMutation = useUpdateConnectionMutation();
  const activeOrganization = useActiveOrganization();

  const [jsonError, setJsonError] = useState<string | null>(null);
  const [selectedNodeType, setSelectedNodeType] = useState<NodeType | null>(null);
  const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);

  const { data: nodeResources } = useNodeResources();

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')          // Replace spaces with hyphens
      .replace(/-+/g, '-')           // Replace multiple hyphens with single
      .trim();
  };

  const isEditMode = mode === 'edit';
  const mutation = isEditMode ? updateConnectionMutation : createConnectionMutation;

  // Initialize form when modal opens
  useEffect(() => {
    if (!visible) return;

    if (isEditMode && connection) {
      // Edit mode: populate with connection data
      form.setFieldsValue({
        name: connection.name,
        slug: connection.slug || '',
        nodetype: connection.nodetype_uuid,
        environment: connection.environment || 'DEV',
        data: connection.data ? JSON.stringify(connection.data, null, 2) : '{}',
      });
      setJsonError(null);

      // Set initial selected node type
      if (connection.nodetype_uuid && nodeResources) {
        const nodeType = nodeResources.node_type_resources?.find(
          (nt: NodeType) => nt.uuid === connection.nodetype_uuid
        );
        setSelectedNodeType(nodeType || null);
      }
    } else {
      // Create mode: reset and set defaults
      form.resetFields();
      form.setFieldsValue({
        name: '',
        slug: '',
        data: '{}',
        environment: 'DEV',
      });
      setJsonError(null);
      setSelectedNodeType(null);
      setIsSlugManuallyEdited(false);
    }
  }, [visible, isEditMode, connection, form, nodeResources]);

  const validateJson = (value: string) => {
    if (!value) return true;
    try {
      JSON.parse(value);
      setJsonError(null);
      return true;
    } catch (error) {
      setJsonError(_(msg`Invalid JSON format`));
      return false;
    }
  };

  const handleNodeTypeChange = (nodeTypeUuid: string) => {
    if (!nodeResources) return;

    const selectedNodeType = nodeResources.node_type_resources?.find(
      (nt: NodeType) => nt.uuid === nodeTypeUuid
    );

    if (selectedNodeType) {
      setSelectedNodeType(selectedNodeType);

      const generatedName = selectedNodeType.name;

      // In edit mode, only prefill template if data hasn't been set yet
      const shouldPrefillData = isEditMode
        ? form.getFieldValue('data') === '{}' || !form.getFieldValue('data')
        : true;

      const templateData = shouldPrefillData && selectedNodeType.connection_data_template
        ? JSON.stringify(selectedNodeType.connection_data_template, null, 2)
        : form.getFieldValue('data') || '{}';

      // Generate slug from name if not manually edited
      const generatedSlug = !isSlugManuallyEdited ? generateSlug(generatedName) : form.getFieldValue('slug');

      form.setFieldsValue({
        name: generatedName,
        slug: generatedSlug,
        data: templateData,
      });
      setJsonError(null);
    }
  };

  const handleSubmit = async () => {
    if (!activeOrganization && !isEditMode) {
      message.error(_(msg`No organization selected`));
      return;
    }

    if (isEditMode && !connection) {
      message.error(_(msg`No connection selected`));
      return;
    }

    try {
      const values = await form.validateFields();

      if (values.data && !validateJson(values.data)) {
        return;
      }

      const parsedData = values.data ? JSON.parse(values.data) : {};

      if (isEditMode && connection) {
        // Edit mode
        const updatePayload: {
          name: string;
          data: { [key: string]: unknown };
          slug?: string;
          environment?: string;
          nodetype?: string;
        } = {
          name: values.name,
          data: parsedData,
        };

        if (values.slug !== undefined && values.slug !== connection.slug) {
          updatePayload.slug = values.slug || null;
        }

        if (values.nodetype && values.nodetype !== connection.nodetype_uuid) {
          updatePayload.nodetype = values.nodetype;
        }

        if (values.environment && values.environment !== connection.environment) {
          updatePayload.environment = values.environment;
        }

        updateConnectionMutation.mutate(
          {
            connectionUuid: connection.uuid,
            data: updatePayload,
          },
          {
            onSuccess: () => {
              message.success(_(msg`Connection updated successfully`));
              setIsSlugManuallyEdited(false);
              if (onSuccess) onSuccess();
              onClose();
            },
          }
        );
      } else {
        // Create mode
        createConnectionMutation.mutate(
          {
            name: values.name,
            slug: values.slug || undefined,
            nodetype: values.nodetype,
            organization: activeOrganization!.uuid,
            environment: values.environment,
            data: parsedData,
          },
          {
            onSuccess: () => {
              setIsSlugManuallyEdited(false);
              if (onSuccess) onSuccess();
              onClose();
            },
          }
        );
      }
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} connection:`, error);
    }
  };

  const handleSlugChange = () => {
    setIsSlugManuallyEdited(true);
  };

  const handleCancel = () => {
    form.resetFields();
    setIsSlugManuallyEdited(false);
    onClose();
  };

  const modalTitle = isEditMode ? (
    <Trans>Edit Connection</Trans>
  ) : (
    <Trans>Create New Connection</Trans>
  );

  const submitButtonText = isEditMode ? (
    <Trans>Save Changes</Trans>
  ) : (
    <Trans>Create Connection</Trans>
  );

  return (
    <Modal
      title={
        <div className={styles.modalHeader}>
          <span className={styles.modalIconContainer}>
            {selectedNodeType?.icon ? (
              <img
                src={selectedNodeType.icon}
                alt={selectedNodeType.name}
                className={styles.iconImage}
              />
            ) : (
              <ApiOutlined className={styles.defaultIcon} />
            )}
          </span>
          <span>
            {modalTitle}
            {selectedNodeType && ` - ${selectedNodeType.name}`}
          </span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={mutation.isLoading}>
          <Trans>Cancel</Trans>
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={mutation.isLoading}
        >
          {submitButtonText}
        </Button>,
      ]}
      width={600}
      className={styles.modal}
    >
      <Form form={form} layout="vertical" className={styles.form}>
        {/* Hidden field for connection name */}
        <Form.Item
          name="name"
          hidden
          rules={[
            { required: true, message: _(msg`Connection name is required`) },
            { max: 50, message: _(msg`Connection name must be less than 50 characters`) },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={_(msg`App`)}
          name="nodetype"
          rules={[{ required: true, message: _(msg`App is required`) }]}
        >
          <Select
            placeholder={_(msg`Select app`)}
            loading={!nodeResources}
            showSearch
            optionLabelProp="label"
            onChange={handleNodeTypeChange}
            disabled={isEditMode}
            filterOption={(input, option) => {
              const name = option?.label?.toString() || '';
              return name.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {nodeResources?.node_type_resources?.map((nodeType: NodeType) => (
              <Select.Option key={nodeType.uuid} value={nodeType.uuid} label={nodeType.name}>
                <span className={styles.integrationOption}>
                  <span className={styles.iconContainer}>
                    {nodeType.icon ? (
                      <img
                        src={nodeType.icon}
                        alt={nodeType.name}
                        className={styles.iconImage}
                      />
                    ) : (
                      <ApiOutlined className={styles.defaultIcon} />
                    )}
                  </span>
                  <span>{nodeType.name}</span>
                </span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={_(msg`Environment`)}
          name="environment"
          rules={[{ required: true, message: _(msg`Environment is required`) }]}
        >
          <Select placeholder={_(msg`Select environment`)}>
            <Select.Option value="DEV">
              <span className={styles.environmentOption}>
                <span className={`${styles.environmentBadge} ${styles.dev}`} />
                <Trans>Development</Trans>
              </span>
            </Select.Option>
            <Select.Option value="TEST">
              <span className={styles.environmentOption}>
                <span className={`${styles.environmentBadge} ${styles.test}`} />
                <Trans>Test</Trans>
              </span>
            </Select.Option>
            <Select.Option value="PROD">
              <span className={styles.environmentOption}>
                <span className={`${styles.environmentBadge} ${styles.prod}`} />
                <Trans>Production</Trans>
              </span>
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label={_(msg`Slug`)}
          name="slug"
          tooltip={_(msg`Unique identifier for cross-environment imports. Use the same slug across DEV, TEST, and PROD. Automatically generated from connection name.`)}
          rules={[
            { max: 255, message: _(msg`Slug must be less than 255 characters`) },
            {
              pattern: /^[a-z0-9-]+$/,
              message: _(msg`Slug can only contain lowercase letters, numbers, and hyphens`)
            }
          ]}
        >
          <Input
            placeholder={_(msg`e.g., crm-salesforce-connection`)}
            className={styles.slugInput}
            onChange={handleSlugChange}
          />
        </Form.Item>

        {/* Hidden fields - values still sent in API request */}
        <Form.Item name="organization" hidden>
          <Input />
        </Form.Item>

        <Form.Item name="user" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          label={_(msg`Connection Data (JSON)`)}
          name="data"
          validateStatus={jsonError ? 'error' : ''}
          help={jsonError || _(msg`Enter connection configuration as JSON (optional)`)}
        >
          <TextArea
            rows={8}
            placeholder={_(msg`Enter JSON data`)}
            className={styles.jsonTextarea}
            onChange={(e) => e.target.value && validateJson(e.target.value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ConnectionModal;
